import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { ArrowRepeat } from 'react-bootstrap-icons';
import { useAuth } from '../../context/AuthContext';
import { switchRole } from '../../api';
import '../../styles/login.css';

const ROLE_LABEL = {
  teacher: 'Docente',
  utp: 'Jefe UTP',
  admin: 'Administrador',
  master: 'Vista Maestra',
};

const SchoolIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 10v6M2 10l10-5 10 5-10 5z" />
    <path d="M6 12v5c3 3 9 3 12 0v-5" />
  </svg>
);

/**
 * Botón "Cambiar Vista": solo se renderiza si la persona autenticada tiene
 * más de un rol activo en el mismo establecimiento (user.available_roles,
 * calculado por el backend al hacer login/select_tenant/switch_role). Abre un
 * modal con las mismas cards que la pantalla de selección del login; elegir
 * una distinta a la vista actual pide un token nuevo con ese rol y recarga la
 * página completa (cada vista mantiene su propio estado en memoria/contexto,
 * así que un cambio de rol arranca todo limpio).
 */
const SwitchViewButton = () => {
  const { user, login } = useAuth();
  const [show, setShow] = useState(false);
  const [switchingId, setSwitchingId] = useState(null);
  const [error, setError] = useState('');

  const roles = user?.available_roles || [];
  if (roles.length <= 1) return null;

  const closeModal = () => {
    if (switchingId !== null) return;
    setShow(false);
    setError('');
  };

  const handleSelect = async (role) => {
    if (switchingId !== null || role.organization_person_role_id === user.organization_person_role_id) {
      return;
    }
    setError('');
    setSwitchingId(role.organization_person_role_id);
    try {
      const data = await switchRole(role.organization_person_role_id);
      login(data.access_token, data.user);
      // Recarga completa: cada vista trae su propio estado en contexto/caché
      // (AdminCacheContext, CurriculumContext, etc.) que debe arrancar limpio
      // para el rol nuevo, no sobrevivir a un cambio de vista en memoria.
      window.location.reload();
    } catch (err) {
      setError(err.message || 'No se pudo cambiar de vista.');
      setSwitchingId(null);
    }
  };

  return (
    <>
      <button
        type="button"
        className="artemis-soporte-btn"
        onClick={() => setShow(true)}
        title="Cambiar entre tus roles en este establecimiento"
      >
        <ArrowRepeat size={16} />
        <span>Cambiar Vista</span>
      </button>

      <Modal show={show} onHide={closeModal} dialogClassName="modal-artemis" centered>
        <Modal.Header closeButton>
          <Modal.Title>Cambiar Vista</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <div className="login-error mb-3" role="alert">
              <span>{error}</span>
            </div>
          )}
          <div className="login-tenant-list mb-0">
            {roles.map((role, i) => {
              const isCurrent = role.organization_person_role_id === user.organization_person_role_id;
              return (
                <button
                  key={role.organization_person_role_id}
                  type="button"
                  className="login-tenant-card"
                  style={{ animationDelay: `${i * 60}ms` }}
                  onClick={() => handleSelect(role)}
                  disabled={switchingId !== null || isCurrent}
                >
                  <span className="login-tenant-icon">
                    {switchingId === role.organization_person_role_id
                      ? <span className="login-spinner login-spinner-dark" />
                      : <SchoolIcon />}
                  </span>
                  <span className="login-tenant-text">
                    <span className="login-tenant-name">{ROLE_LABEL[role.user_type] || role.role_name}</span>
                    {isCurrent && (
                      <span className="login-tenant-role">Vista actual</span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default SwitchViewButton;
